/**
 * Bereiche + Views + Groups — statische Navigation-Definition (Phase 3.5 §5).
 *
 * 2-stufige Sub-Sidebar-Struktur:
 *   - Bereiche (Rail-Items) — z.B. "Vertrieb", "Finance", "HR"
 *   - Groups (Sub-Sidebar Top-Level) — z.B. "Calls", "Meetings", "Follow Ups"
 *     · Klick auf Group-Header navigiert zur Default-Sub-View (Option A)
 *     · Active-Highlight wenn current view in der Group liegt
 *   - Views (Sub-Tabs unter Group) — z.B. "Overview", "Bilanz", "Pflege"
 *
 * Standalone-Items (z.B. Übersicht, Pflegelücken, Ziele):
 *   Group mit genau 1 View → renderbar als Single-Link ohne Sub-Tab-Block.
 *
 * Routes bleiben flat (Option 1): `/caller`, `/meetings/kalender`,
 * `/pflegeluecken` — Sub-Sidebar-Anzeige ist gruppiert, URLs unverändert.
 */

import type { UserContext } from "../api/types";
import { isArenaLive, isLigaLive, istBuchungSichtbar } from "./feature-flags";

export type AktiveFunktion = "Caller" | "Host_MT" | "Host_FU";

export type ViewDef = {
  key: string;
  label: string;
  route: string;
  requiresAktiv?: AktiveFunktion[];
  adminOnly?: boolean;
  comingSoon?: boolean;
  /**
   * Wenn true: View ist nicht als Sub-Tab in der Sidebar sichtbar, sondern
   * nur als Group-Header-Klick-Ziel (Default-View). Spart Sub-Sidebar-Höhe,
   * vermeidet doppelte "Overview"-Einträge. View bleibt aber in
   * flattenedViews/findViewForRoute, damit Routes auflösbar bleiben.
   */
  hideAsSubTab?: boolean;
  /**
   * 🔴 **Zu welchem SICHTBAREN Eintrag dieser versteckte Reiter gehört.**
   *
   * 📏 Der Anlass, gemeldet von Justin am 22.09.2026: *„unter Prüfen, wenn ich
   * auf Absprachen gehe, ist in der Sidebar noch Vorgänge ausgewählt."*
   * Und es traf nicht nur den neuen Reiter — `/widerrufe/rechte` und
   * `/widerrufe/luecke` hatten denselben Fehler, seit es sie gibt.
   *
   * **Warum:** `findViewForRoute` findet für `/widerrufe/rechte` keinen exakten
   * Eintrag und fällt auf den längsten Präfix zurück — das ist `/widerrufe`,
   * also **„Vorgänge"**. Die Leiste hebt danach den Eintrag hervor, auf dem man
   * gerade NICHT ist.
   * 🔑 ***Eine Leiste, die den falschen Eintrag hervorhebt, ist schlimmer als
   * eine, die keinen hervorhebt: die erste lügt, die zweite schweigt.***
   *
   * ⚠️ Der Präfix-Rückfall ist nicht der Fehler — er ist die beste Auskunft,
   * die aus der Adresse allein zu holen ist. Der Fehler war, dass die
   * Zugehörigkeit **nirgends stand**. *Was ein Algorithmus raten muss, gehört
   * ins Datenmodell.*
   */
  reiterVon?: string;
  /**
   * Additiv (Finance-Subsidebar v3): pro-View-Icon (Lucide-Key aus icons.ts),
   * das im Sub-Tab vor dem Label gerendert wird. Wenn nicht gesetzt → bisheriges
   * Verhalten (kein Icon). Nur Finance nutzt das aktuell.
   */
  iconKey?: string;
  /**
   * Additiv (Finance-Subsidebar v3): optionales Tag am Zeilenende.
   * kind "neu" = lime (--accent), "f" = Feature-Code orange (--bereich-finance).
   * Wenn nicht gesetzt → kein Tag (andere Bereiche unverändert).
   */
  tag?: { label: string; kind: "neu" | "f" };
};

export type GroupDef = {
  key: string;
  label: string;
  iconKey: string;
  /** Route bei Group-Header-Klick. Default: erste nicht-coming-soon View. */
  defaultRoute?: string;
  views: ViewDef[];
  /** Group ganz versteckt für non-Admin/non-BL. */
  adminOnly?: boolean;
  /** Group nur für User mit dieser Funktion sichtbar. */
  requiresAktiv?: AktiveFunktion[];
  /**
   * Group **nur für genau diese Person** sichtbar (`person_id`, z. B. "P-019").
   *
   * Anders als `adminOnly` gilt hier **keine** Admin-Hintertür: Eine persönliche
   * Ansicht ist persönlich, auch für Admins. Das Backend gated identisch — wer
   * den Eintrag nicht sieht, wird auch von der API abgewiesen.
   *
   * 🔑 Gebaut für JV-9 (`/mein/protokolle`), weil die Alternative `adminOnly`
   * allen anderen Admins einen Menüpunkt gezeigt hätte, der sie abweist —
   * genau das „Angebot, das ins Leere führt", das schon bei *Sperrzeiten*
   * vermieden wurde.
   */
  requiresPerson?: string;
  /**
   * Group nur für Admin ODER Bereichsleitung DIESES Bereichs (Slug). Anders als
   * `adminOnly`, das an der Vertriebsleitung hängt. 🆕 02.11.2026 für
   * „SEPA-Papiermandate“ (Finance): die Liste trägt Vereine und IBAN-Endziffern,
   * der Server lässt nur Bereichsleitung Finance + Admin durch — der Eintrag
   * soll niemandem ein Angebot machen, das ins Leere führt.
   */
  requiresBl?: string;
  /** Group noch nicht freigeschaltet (komplett). */
  comingSoon?: boolean;
  /**
   * Wenn true: Group ist eine Bereichs-Einstellungs-Gruppe. Sie wird NICHT in
   * der normalen Nav-Liste der Sub-Sidebar gerendert, sondern über ein Zahnrad
   * im Sub-Sidebar-Head + eine Tab-Leiste in der Container-View erreicht.
   * Views bleiben in flattenedViews/findViewForRoute auflösbar.
   */
  isSettings?: boolean;
};

export type BereichDef = {
  slug: string;
  label: string;
  short: string;
  iconKey: string;
  /** Hex-Farbe für Hub-Bereich-Tile-Badge. */
  color: string;
  requiresBereich?: string;
  /** Sichtbar wenn isAdmin ODER User in ≥1 Bereich Bereichsleiter ist (Admin-Bereich 3.7). */
  requiresAdminOrBl?: boolean;
  /** Sichtbar wenn isAdmin ODER Bereichsleiter DIESES Bereichs (Slug) — z.B. HR-Manage. */
  requiresBl?: string;
  /** Nur für isAdmin sichtbar (admin-only Bereich wie DQ — passt zum admin-only Backend). */
  requiresAdmin?: boolean;
  /** Bereich ist insgesamt noch nicht freigeschaltet. */
  comingSoon?: boolean;
  /** 2-stufige Struktur (Vertrieb). Wenn vorhanden → groups rendern, sonst views. */
  groups?: GroupDef[];
  /** Flat-Views (Bereiche ohne 2-stufige Struktur — Hub, Mein-Bereich, ...). */
  views: ViewDef[];
};

/**
 * DIE GRUPPE „WIDERRUFE" — dreimal dieselbe, einmal geschrieben (18.09.2026).
 *
 * > **Justin, 17.09.:** *„Ich möchte nochmal an der Übersichtlichkeit arbeiten,
 * > finde es noch recht voll … wirkt noch sehr unaufgeräumt."*
 * > **Justin, 17.09.:** *„Vorgänge und Prüfung finde ich glaube nicht schlecht."*
 *
 * Bis hierher war „Widerrufe" **ein Eintrag** unter einer fremden Gruppe —
 * unter *Closings* im Vertrieb, unter *Aufgaben* in der KB, unter den
 * *Analysen* in Finance — und dahinter lagen **sechs** Reiter in einer Zeile.
 * 🔑 ***Ein Menüeintrag, hinter dem sechs ungleiche Reiter liegen, verschiebt
 * die Gliederung auf die Seite — und die Seite kann sie nicht tragen.***
 *
 * ⚠️ **In allen drei Bereichen identisch**, und zwar mit Absicht: der Vorgang
 * gehört Vertrieb, Kundenbetreuung und Finance gemeinsam. Sähe er in jedem
 * Bereich anders aus, entstünden drei Kenntnisstände über eine Sache — genau
 * das, was der gemeinsame Slack-Kanal seit dem 15.09. verhindert.
 * ⇒ Deshalb **eine** Funktion und drei Aufrufe, keine drei Abschriften.
 *
 * 📌 **„Dashboard" ist seit dem 18.09. drin** — und war es bis dahin bewusst
 * nicht, weil die Ansicht noch nicht existierte. 🔑 *Ein Menüeintrag, der ins
 * Leere führt, ist schlimmer als ein fehlender — er kostet einen Klick und eine
 * Enttäuschung, und danach glaubt niemand mehr der Leiste.*
 * ⚠️ Er zeigt **einen** von fünf Abschnitten des Entwurfs; welche vier fehlen,
 * sagt die Seite selbst an ihrem Fuss — nicht dieser Kommentar. *Eine Liste des
 * Fehlenden im Code veraltet, eine auf dem Schirm sieht jeder.*
 */
function widerrufsGruppe(basis: "/widerrufe" | "/kb/widerrufe" | "/finance/widerrufe"): GroupDef {
  const p = basis === "/widerrufe" ? "" : basis === "/kb/widerrufe" ? "kb-" : "finance-";
  return {
    key: `${p}widerrufe-group`,
    label: "Widerrufe",
    iconKey: "file-x",
    /**
     * 🔄 **25.09.2026, Justin:** *„wenn ich auf widerrufe klicke will ich aufs
     * dashboard kommen und nicht auf vorgänge — dashboard braucht dann auch
     * keinen eigenen sidebar eintrag."*
     *
     * Vorher stand hier `basis` mit der Begründung *„dort liegt die Arbeit"*.
     * Das stimmt für den, der schon weiss, was er tun will — der Klick auf den
     * **Bereich** stellt aber die Frage davor: *wie steht es?*
     * 🔑 ***Ein Bereich beginnt mit der Lage, nicht mit der ersten Aufgabe.***
     */
    defaultRoute: `${basis}/dashboard`,
    views: [
      /**
       * ⚠️ `hideAsSubTab` — das Dashboard steht **nicht mehr** in der Leiste:
       * es ist das Ziel des Gruppenklicks darüber. *Ein Eintrag, der dasselbe
       * tut wie die Zeile über ihm, ist kein zweiter Weg, sondern eine
       * Verdopplung.*
       *
       * 🔴 Der Eintrag BLEIBT aber stehen, nur unsichtbar: nähme man ihn ganz
       * heraus, fiele `/widerrufe/dashboard` auf den längsten Präfix zurück —
       * `/widerrufe` — und die Leiste hübe **„Vorgänge"** hervor, während man
       * im Dashboard steht. Genau dieser Fehler war am 22.09. schon einmal da.
       * 🔑 *Eine Leiste, die den falschen Eintrag hervorhebt, ist schlimmer als
       * eine, die keinen hervorhebt: die erste lügt, die zweite schweigt.*
       */
      { key: `${p}widerrufe-dashboard`, label: "Dashboard", route: `${basis}/dashboard`,
        hideAsSubTab: true },
      // ⚠️ `hideAsSubTab` NICHT gesetzt: die beiden Unterkategorien sollen
      // sichtbar sein. Sie sind der ganze Zweck des Umbaus.
      { key: `${p}widerrufe`, label: "Vorgänge", route: basis },
      /**
       * 🔑 **Der Einstieg in „Prüfung" ist der Abgleich, nicht die erste Zeile
       * der Liste.** Er beantwortet die Frage, wegen der man herkommt („läuft
       * etwas auseinander"); „Offene Widerrufsrechte" und „Frist nicht
       * bestimmbar" sind Sonderfälle davon.
       */
      { key: `${p}widerrufe-pruefung`, label: "Prüfung", route: `${basis}/abgleich` },
      /**
       * 🔴 **Die übrigen Prüfungs-Reiter, damit die Leiste den RICHTIGEN
       * Eintrag hervorhebt** (Justin, 22.09.2026). Sie sind Reiter in der
       * Ansicht, nicht Zeilen der Leiste — deshalb `hideAsSubTab` — und sie
       * bekennen sich über `reiterVon` zu ihrer Gruppe.
       *
       * 🔑 **NUR die Prüfungs-Reiter stehen hier, und das ist kein Vergessen.**
       * Für alles unter „Vorgänge" (`/offen`, `/abgeschlossen`, `/melden`,
       * `/fall/…`) trifft der Präfix-Rückfall bereits den richtigen Eintrag —
       * dort **ist** die Basis der sichtbare Eintrag. Ein Eintrag, der dasselbe
       * Ergebnis noch einmal von Hand festschreibt, ist eine zweite Wahrheit,
       * die beim nächsten Umbau nicht mitwandert.
       */
      { key: `${p}widerrufe-absprachen`, label: "Absprachen prüfen", route: `${basis}/absprachen`, hideAsSubTab: true, reiterVon: `${p}widerrufe-pruefung` },
      { key: `${p}widerrufe-rechte`, label: "Offene Widerrufsrechte", route: `${basis}/rechte`, hideAsSubTab: true, reiterVon: `${p}widerrufe-pruefung` },
      { key: `${p}widerrufe-luecke`, label: "Frist nicht bestimmbar", route: `${basis}/luecke`, hideAsSubTab: true, reiterVon: `${p}widerrufe-pruefung` },
      /**
       * 🔄 **05.10.2026 — „Liste" als eigener Eintrag**, vorher ein Reiter unter
       * „Vorgänge". > Justin: *„Liste bei Widerrufe und Kündigungen gerne als
       * eigener Punkt in die Sidebar und raus aus den Tabs."*
       * 🔑 Sie beantwortet eine eigene Frage (was war in einem Zeitraum) mit
       * eigenem Schirm — wie die Closingliste, die auch hier in der Leiste steht.
       */
      { key: `${p}widerrufe-liste`, label: "Liste", route: `${basis}/liste` },
      /**
       * 🆕 **04.10.2026 — E17: „Quote je Closer" als eigener Eintrag**, in
       * **beiden** Registern. Die Ansicht gibt es seit dem 10.09. unter
       * `/quote` und war nur über die Adresszeile erreichbar.
       * 🔑 ***Eine gebaute Ansicht ohne Weg dorthin ist nicht gebaut.***
       */
      { key: `${p}widerrufe-quote`, label: "Quote je Closer", route: `${basis}/quote` }
    ]
  };
}

/**
 * KÜNDIGUNGSREGISTER (Punkt 18) — dieselbe Machart wie `widerrufsGruppe`.
 *
 * **Justin, 21.09.2026:** *„Kündigungsregister soll eigener Sidebar-Punkt werden
 * wie widerrufe."* ⇒ eine eigene Gruppe in **allen drei** Bereichen, nicht ein
 * Untereintrag der Kundenbetreuung.
 *
 * 🔑 Der Vorgang gehört Vertrieb, Kundenbetreuung und Finance gemeinsam — es
 * sind dieselben drei, die auch quittieren. *Eine Gruppe, die nur in einem
 * Bereich steht, macht aus einer gemeinsamen Arbeit eine fremde.*
 *
 * 📌 Die vier Unterwege tragen `hideAsSubTab`: sie sind **Reiter in der
 * Ansicht**, keine Zeilen der Leiste. Sie stehen hier nur, damit jede
 * registrierte Adresse einen Bedienweg auflöst — 🔑 *„im Bundle vorhanden"
 * heisst nicht „erreichbar", und genau das hat der Nav-Wächter gemeldet, als
 * die Routen zuerst ohne Eintrag dastanden.*
 *
 * ⏭️ Es fehlt noch der Eintrag **„Fall"** — die Detailansicht ist im Bau.
 * Ein Menüeintrag, der ins Leere führt, ist schlimmer als ein fehlender.
 */
function kuendigungsGruppe(
  basis: "/kuendigungen" | "/kb/kuendigungen" | "/finance/kuendigungen"
): GroupDef {
  const p = basis === "/kuendigungen" ? "" : basis === "/kb/kuendigungen" ? "kb-" : "finance-";
  return {
    key: `${p}kuendigungen-group`,
    label: "Kündigungen",
    iconKey: "file-minus",
    /**
     * 🔄 **28.09.2026 berichtigt.** Hier stand `basis` — ein Klick auf
     * „Kündigungen" landete auf **Vorgänge**, während derselbe Klick auf
     * „Widerrufe" aufs Dashboard führt.
     *
     * Justins Entscheid dazu ist vom **25.09.2026** und steht wörtlich an der
     * Widerrufs-Gruppe zwanzig Zeilen weiter oben: ▎ *„wenn ich auf widerrufe
     * klicke will ich aufs dashboard kommen und nicht auf vorgänge"*.
     * Das Dashboard und sein versteckter Nav-Eintrag wurden am 28.09. von dort
     * übernommen — **die `defaultRoute` nicht.**
     *
     * 🔑 ***Der Eintrag wurde übernommen, der Entscheid dahinter nicht.***
     * Und das ist zum dritten Mal an einem Tag dieselbe Form: *eine Regel, die
     * nur in einem der beiden Register steht, ist keine Regel des Hauses.*
     * 🔑 *Ein Bereich beginnt mit der Lage, nicht mit der ersten Aufgabe.*
     */
    defaultRoute: `${basis}/dashboard`,
    /**
     * ═══ 🔄 22.09.2026 — ZWEI GRUPPEN STATT EINER REITERZEILE MIT FÜNF ══════
     *
     * Dieselbe Korrektur, die der Widerruf am 18.09. bekommen hat, und aus
     * demselben Grund. Dort standen **sechs** Reiter in einer Zeile; hier
     * waren es **fünf** — und sie beantworten **zwei** verschiedene Fragen:
     *
     *   **Vorgänge** · „woran arbeite ich?"  → Register · Anwärter
     *   **Prüfung**  · „läuft etwas auseinander?" → Unstimmig · Abweichung · Archiv
     *
     * 🔑 ***Eine Reiterzeile ist eine Behauptung, die Einträge seien
     * gleichartig.*** Fünf nebeneinander behaupten das fünfmal — und zwingen
     * den Leser, die Gliederung selbst zu erfinden.
     *
     * ⚠️ „Abweichung" ist **keine Arbeitsliste**: sie zeigt, was die berichtigte
     * Regel ändern WÜRDE. Unter den Vorgängen gelesen, sieht das aus wie ein
     * Stand. *Eine Vorlage, die zwischen Arbeitslisten steht, wird für den
     * Stand gehalten.*
     *
     * 📌 Der Einstieg in „Prüfung" ist **Unstimmig**, nicht die erste Zeile der
     * Gruppe: dort steht der Widerspruch, wegen dem man herkommt.
     */
    views: [
      /**
       * 🆕 28.09.2026 — das Dashboard, wie beim Widerruf seit dem 22.09.
       *
       * ⚠️ `hideAsSubTab` gesetzt, der Eintrag **bleibt aber stehen**: nähme man
       * ihn ganz heraus, fiele `/kuendigungen/dashboard` auf den längsten
       * Präfix `/kuendigungen` zurück, und die Leiste höbe **„Vorgänge"**
       * hervor, während man im Dashboard steht.
       * 🔑 *Eine Leiste, die den falschen Eintrag hervorhebt, ist schlimmer als
       * eine, die keinen hervorhebt: die erste lügt, die zweite schweigt.*
       * 📏 Genau dieser Fehler war beim Widerruf am 22.09. schon einmal da.
       */
      { key: `${p}kuendigungen-dashboard`, label: "Dashboard", route: `${basis}/dashboard`,
        hideAsSubTab: true },
      { key: `${p}kuendigungen`, label: "Vorgänge", route: basis },
      { key: `${p}kuendigungen-pruefung`, label: "Prüfung", route: `${basis}/unstimmig` },
      /**
       * 🆕 26.09.2026 — **„Weiterhin offen"**, der Gegenreiter zu „Bei mir".
       *
       * 🔑 Er steht hier und **nicht** in der Ausnahmeliste von
       * `nav-routen-parity`: die anderen vier Kündigungs-Reiter stehen auch
       * hier. *Zwei Wege, dieselbe Art Route zu rechtfertigen, sind ein Weg zu
       * viel — und beim nächsten Reiter wählt jemand den anderen.*
       * ⚠️ Der Widerruf macht es umgekehrt (`/widerrufe/offen` steht in der
       * Ausnahmeliste). Das bleibt vorerst so: es hier anzugleichen wäre eine
       * Änderung an seiner Nav, und die gehört nicht in diesen Schritt.
       */
      { key: `${p}kuendigungen-offen`, label: "Weiterhin offen", route: `${basis}/offen`, hideAsSubTab: true, reiterVon: `${p}kuendigungen` },
      { key: `${p}kuendigungen-anwaerter`, label: "Anwärter", route: `${basis}/anwaerter`, hideAsSubTab: true, reiterVon: `${p}kuendigungen` },
      { key: `${p}kuendigungen-unstimmig`, label: "Unstimmig", route: `${basis}/unstimmig`, hideAsSubTab: true, reiterVon: `${p}kuendigungen-pruefung` },
      { key: `${p}kuendigungen-abweichung`, label: "Abweichung", route: `${basis}/abweichung`, hideAsSubTab: true, reiterVon: `${p}kuendigungen-pruefung` },
      /**
       * 🆕 26.09.2026 — „Absprachen prüfen", wie beim Widerruf seit dem 22.09.
       * 🔑 In der Gruppe **Prüfung**: eine Absprache betrifft die Regel eines
       * Vereins, nicht einen Fall.
       */
      { key: `${p}kuendigungen-absprachen`, label: "Absprachen prüfen", route: `${basis}/absprachen`, hideAsSubTab: true, reiterVon: `${p}kuendigungen-pruefung` },
      /**
       * 🔄 **04.10.2026 — E13.** Hiess „Archiv" und gehörte zu **Prüfung**;
       * heisst jetzt „Abgeschlossen" und gehört zu **Vorgängen**, wie im
       * Widerruf. ⚠️ Der **Pfad bleibt** `/archiv`: die Vorgabe nennt den
       * Reiter, nicht die Adresse, und ein Pfadwechsel bricht gespeicherte
       * Links still.
       * 🔑 *Ein Etikett ändert, was jemand liest; ein Pfad ändert, wohin ein
       * Lesezeichen führt — das sind zwei verschiedene Entscheidungen.*
       */
      { key: `${p}kuendigungen-archiv`, label: "Abgeschlossen", route: `${basis}/archiv`, hideAsSubTab: true, reiterVon: `${p}kuendigungen` },
      // 🔄 05.10.2026 — „Liste" als eigener Eintrag, wie beim Widerruf (Begründung dort).
      { key: `${p}kuendigungen-liste`, label: "Liste", route: `${basis}/liste` },
      /**
       * 🆕 **04.10.2026 — E17: „Quote je Closer" als eigener Eintrag.**
       *
       * Die Ansicht gibt es seit dem 28.09. unter `/quote`, erreichbar war sie
       * nur über die Adresszeile. 🔑 ***Eine gebaute Ansicht ohne Weg dorthin
       * ist nicht gebaut*** — sie ist Vorrat.
       * ⚠️ Sichtbar (kein `hideAsSubTab`): sie ist keine Unterform von
       * „Vorgänge" oder „Prüfung", sondern eine dritte Frage — *wer schliesst
       * ab, und was hält*.
       */
      { key: `${p}kuendigungen-quote`, label: "Quote je Closer", route: `${basis}/quote` }
    ]
  };
}

/**
 * VERTRAGSANPASSUNGEN (Deckblatt-Bau § 4.8, 04.10.2026) — das Register der Abreden an
 * einem Vertrag: erfassen (schreibt die Notiz nach HubSpot), bestätigen (Bereichsleitung),
 * protokollieren. Bestätigte Anpassungen stehen im Vertragsdeckblatt.
 *
 * 🔑 In denselben drei Bereichen wie Kündigung und Widerruf — dieselben drei, die eine
 * Abrede treffen. *Eine Gruppe, die nur in einem Bereich steht, macht aus einer
 * gemeinsamen Arbeit eine fremde.*
 */
function vertragsanpassungsGruppe(
  basis: "/vertragsanpassungen" | "/kb/vertragsanpassungen" | "/finance/vertragsanpassungen"
): GroupDef {
  const p = basis === "/vertragsanpassungen" ? "" : basis === "/kb/vertragsanpassungen" ? "kb-" : "finance-";
  return {
    key: `${p}vertragsanpassungen-group`,
    label: "Vertragsanpassungen",
    iconKey: "file-text",
    defaultRoute: basis,
    views: [
      { key: `${p}vertragsanpassungen`, label: "Offen", route: basis },
      { key: `${p}vertragsanpassungen-erfassen`, label: "Erfassen", route: `${basis}/erfassen` },
      { key: `${p}vertragsanpassungen-alle`, label: "Alle", route: `${basis}/alle` },
      { key: `${p}vertragsanpassungen-altvertrag`, label: "Altvertrag erfassen", route: `${basis}/altvertrag` },
      { key: `${p}vertragsanpassungen-nachzug`, label: "Nachzug", route: `${basis}/nachzug` }
    ]
  };
}

// =============================================================================
// 9 Apps-Script-Bereiche (Hub + 8 Bereiche), Stand 2026-05-24
// =============================================================================

export const BEREICHE: BereichDef[] = [
  {
    slug: "hub",
    label: "Hub",
    short: "Hub",
    iconKey: "home",
    color: "#D1FD51",
    views: [{ key: "hub", label: "Hub", route: "/" }]
  },
  {
    slug: "meinbereich",
    label: "Mein Bereich",
    short: "Ich",
    iconKey: "user-circle",
    color: "#6366f1",
    groups: [
      { key: "profil", label: "Profil", iconKey: "user-circle", views: [{ key: "profil", label: "Profil", route: "/profil" }] },
      // JV-9 Sekretariat: Justins eigene Protokoll-Entwürfe. Persönlich, deshalb
      // `requiresPerson` statt `adminOnly` — siehe Typkommentar.
      {
        key: "protokolle", label: "Protokolle", iconKey: "file-text",
        requiresPerson: "P-019",
        views: [{ key: "protokolle", label: "Protokolle", route: "/mein/protokolle" }]
      },
      { key: "konto", label: "Konto & Sicherheit", iconKey: "shield-check", views: [{ key: "konto", label: "Konto & Sicherheit", route: "/konto" }] },
      { key: "urlaub", label: "Urlaub", iconKey: "palmtree", views: [{ key: "urlaub", label: "Urlaub", route: "/urlaub" }] },
      { key: "stunden", label: "Stunden", iconKey: "clock", views: [{ key: "stunden", label: "Stunden", route: "/stunden" }] },
      { key: "krank", label: "Krank", iconKey: "heart-pulse", views: [{ key: "krank", label: "Krank", route: "/krank" }] },
      // Baustein 6b: **nur für Hosts** (Justin 14.08.). Ein Caller hostet nichts
      // und hat deshalb auch nichts zum Sperren — der Eintrag erschiene ihm als
      // Angebot, das ins Leere führt. Backend gated identisch.
      {
        key: "sperrzeiten", label: "Sperrzeiten", iconKey: "clock",
        requiresAktiv: ["Host_MT", "Host_FU"],
        views: [{ key: "sperrzeiten", label: "Sperrzeiten", route: "/sperrzeiten" }]
      }
    ],
    views: []
  },
  {
    slug: "vertrieb",
    label: "Vertrieb",
    short: "Vertrieb",
    iconKey: "trending-up",
    color: "#10b981",
    requiresBereich: "vertrieb",
    groups: [
      {
        key: "uebersicht",
        label: "Vertriebs-Übersicht",
        iconKey: "layout-grid",
        views: [
          { key: "uebersicht", label: "Übersicht", route: "/uebersicht" }
        ]
      },
      {
        key: "pflegeluecken-group",
        label: "Pflegelücken",
        iconKey: "alert-triangle",
        views: [
          { key: "pflegeluecken", label: "Pflegelücken", route: "/pflegeluecken" }
        ]
      },
      {
        key: "ziele",
        label: "Ziele",
        iconKey: "target",
        views: [
          { key: "ziele", label: "Ziele", route: "/ziele" },
          // Paket 4 — Vertriebs-Liga (Gamification). Sichtbarkeit hängt am Settings-Flag
          // LIGA_LIVE (R7): live → alle berechtigten Vertriebs-Rollen, sonst nur BL/Admin.
          // Das statische adminOnly wird in isViewVisibleForUser zur Laufzeit überstimmt.
          { key: "vertrieb-liga", label: "Liga", route: "/vertrieb/liga", adminOnly: true },
          // Ausbaustufe 4 — die Arena über der Liga. Eigener Schalter ARENA_LIVE;
          // solange er aus ist, sehen sie nur BL/Admin (und das Backend gatet mit).
          { key: "vertrieb-arena", label: "Arena", route: "/vertrieb/arena", adminOnly: true }
        ]
      },
      // Lead-Agent (§11.1) — eigene Gruppe, nicht unter „Ziele" eingehängt.
      // 📌 Er gehört zu keiner der bestehenden: Ziele misst, was der Vertrieb
      // tut; der Lead-Agent bringt herein, woran er es tut. Als Unterpunkt
      // eines fremden Themas sucht ihn niemand dort, wo er steht.
      // 📌 `adminOnly` ist die ANZEIGE — die Rechte hängen am Backend (drei
      // getrennte: ansehen/freigeben/starten, §11.3), und dort wird jede
      // Handlung erneut geprüft. Solange der Agent im Probebetrieb ist, sieht
      // ihn nur, wer ihn bedienen soll.
      /**
       * 🔄 17.09.2026 — EIN SIDEBAR-PUNKT, DREI EINTRÄGE, DANN TABS (`B1`·`B2`).
       *
       * > **Justin, 16.09., auf die Frage nach dem Zweck:** *„Beides, aber
       * > getrennt."* — neue Vereine finden UND den Bestand pflegen.
       * > **Justin, 17.09., zur Form:** *„Ich hätte das gerne unter einem
       * > Sidebarpunkt zusammengefasst … die Unterpunkte davon dann jeweils
       * > als Tabs."*
       *
       * Vorher standen dreizehn Einträge flach untereinander. Beide Arbeiten
       * waren möglich, aber keine war benannt: der Einstieg sah für beide
       * gleich aus, und wer „Suche starten" las, konnte nicht wissen, ob er
       * damit Neues sucht oder Vorhandenes prüft.
       *
       * 🔑 ***Zwei Aufgaben unter einer Überschrift werden zu einer — und zwar
       * zu der, die oben steht.***
       *
       * ⚠️ **Alle dreizehn Routen bleiben, wie sie waren**, und sind weiterhin
       * einzeln aufrufbar — sie stehen nur nicht mehr in der Seitenleiste,
       * sondern in der Tab-Leiste ihres Werkzeugs (`hideAsSubTab`, dasselbe
       * Muster wie bei den Vertriebseinstellungen).
       * 🔑 *Eine Umsortierung, die Adressen ändert, bricht Lesezeichen — und
       * zwar still.*
       */
      {
        key: "lead-agent",
        label: "Lead-Agent",
        iconKey: "user-check",
        defaultRoute: "/vertrieb/leadagent/neu",
        views: [
          /* Die drei sichtbaren Einträge. Jeder ist ein Behälter mit Tabs. */
          { key: "vertrieb-leadagent-neu", label: "Neue Vereine finden", route: "/vertrieb/leadagent/neu", adminOnly: true },
          { key: "vertrieb-leadagent-pflege", label: "Bestand pflegen", route: "/vertrieb/leadagent/pflege", adminOnly: true },
          { key: "vertrieb-leadagent-lage", label: "Lage", route: "/vertrieb/leadagent/lage", adminOnly: true },

          /* ── Die Tabs. Nicht in der Seitenleiste, aber voll aufrufbar. ──
             ⚠️ Reihenfolge = Reihenfolge der Tab-Leiste; die Zuordnung zum
             Werkzeug steht in `vertrieb-leadagent-werkzeug.ts`, damit sie
             genau einmal existiert. */
          { key: "vertrieb-leadagent-start", label: "Suche starten", route: "/vertrieb/leadagent/start", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-jobs", label: "Jobs", route: "/vertrieb/leadagent/jobs", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent", label: "Vorschläge (neu)", route: "/vertrieb/leadagent", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-luecken", label: "Lücken schließen", route: "/vertrieb/leadagent/luecken", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-handpruefung", label: "Handprüfung", route: "/vertrieb/leadagent/handpruefung", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-landkarte", label: "Abdeckung", route: "/vertrieb/leadagent/landkarte", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-korrekturen", label: "Korrekturliste", route: "/vertrieb/leadagent/korrekturen", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-qualitaet", label: "Leadqualität", route: "/vertrieb/leadagent/qualitaet", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-uebersicht", label: "Übersicht", route: "/vertrieb/leadagent/uebersicht", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-lehren", label: "Gelerntes", route: "/vertrieb/leadagent/lehren", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-protokoll", label: "Laufprotokoll", route: "/vertrieb/leadagent/protokoll", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-leitung", label: "Leitungssicht", route: "/vertrieb/leadagent/leitung", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-sperrliste", label: "Sperrliste", route: "/vertrieb/leadagent/sperrliste", adminOnly: true, hideAsSubTab: true },
          { key: "vertrieb-leadagent-verein", label: "Verein", route: "/vertrieb/leadagent/verein", adminOnly: true, hideAsSubTab: true }
        ]
      },
      {
        key: "calls",
        label: "Calls",
        iconKey: "phone",
        views: [
          { key: "caller", label: "Übersicht", route: "/caller", hideAsSubTab: true },
          { key: "caller-bilanz", label: "Bilanz", route: "/caller/bilanz" },
          { key: "caller-pflege", label: "Pflege", route: "/caller/pflege", requiresAktiv: ["Caller"] }
        ]
      },
      {
        key: "meetings",
        label: "Meetings",
        iconKey: "calendar-check",
        views: [
          { key: "meetings", label: "Übersicht", route: "/meetings", hideAsSubTab: true },
          { key: "meetings-kalender", label: "Kalender", route: "/meetings/kalender" },
          // Baustein 4a — der Weg-ERSATZ für das Eintragen in Zeeg. Er steht
          // bewusst VOR „Slots": der Caller sucht hier den Termin, nicht das
          // Raster (⓪-B: er trägt heute selbst in Zeeg ein und verliert diesen
          // Weg mit der Abschaltung).
          { key: "meetings-buchen", label: "Buchen", route: "/meetings/buchen" },
          { key: "meetings-slots", label: "Slots", route: "/meetings/slots" },
          // ⑧ B2 — die Anfragen ohne Termin. Sie hängen unter „Meetings", weil
          // aus ihnen Meetings werden sollen; eine eigene Oberkategorie hätte
          // sie von der Arbeit getrennt, zu der sie gehören.
          { key: "meetings-anfragen", label: "Anfragen", route: "/meetings/anfragen" }
        ]
      },
      {
        key: "fu",
        label: "Follow Ups",
        iconKey: "repeat",
        views: [
          { key: "fu", label: "Übersicht", route: "/fu", hideAsSubTab: true },
          { key: "fu-kalender", label: "Kalender", route: "/fu/kalender" }
        ]
      },
      {
        // 🆕 A6 Phase 2 · A-2 (05.10.2026, Justin): die Vereinseinrichtung ist die
        // Stufe, in der unterschrieben wird — sie gehört dem Vertrieb, nach dem
        // Vorbild von Meetings und FUs. Dieselben Schirme wie in der KB, fest auf
        // VE (`views/ve/ve-ort.ts`).
        // ✅ Spec B · F4 (08.10.2026): auch VE-Hosts OHNE Vertriebsbereich sehen
        // diese Gruppe — und im Vertrieb nur sie (`istNurVeHostImVertrieb`).
        key: "vereinseinrichtungen",
        label: "Vereinseinrichtungen",
        iconKey: "calendar-days",
        defaultRoute: "/vertrieb/ve",
        views: [
          // Der Einstieg — trägt auch das Termin-Detail `/vertrieb/ve/:id` (längster
          // Präfix), sonst verlöre es die Sub-Sidebar (Wächter `sidebar-aufloesung`).
          { key: "ve-vertrieb", label: "Übersicht", route: "/vertrieb/ve" },
          { key: "ve-vertrieb-kalender", label: "Kalender", route: "/vertrieb/ve/kalender" },
          { key: "ve-vertrieb-buchen", label: "Neue Vereinseinrichtung", route: "/vertrieb/ve/buchen" },
          { key: "ve-vertrieb-zeiten", label: "Meine Zeiten", route: "/vertrieb/ve/zeiten" },
          // Spec A A-2: Zuteilung nur Admin + Vertriebs-BL (`adminOnly` = isBlOrAdmin =
          // Admin oder Vertriebs-BL). Der Server prüft dasselbe (`darfHostPflegen`).
          { key: "ve-vertrieb-zuteilung", label: "Zuteilung", route: "/vertrieb/ve/zuteilung", adminOnly: true }
        ]
      },
      {
        // E11 + Baustein 6b: eigene Oberkategorie (Justin 14.08.), weil sie
        // Meetings UND Follow-Ups betrifft — sie unter „Meetings" zu hängen
        // hätte die FU-Hälfte unsichtbar gemacht. Sichtbar für alle im
        // Vertrieb; WAS jemand sieht, entscheidet der Endpunkt: die BL alle
        // Klärungsfälle, ein Host seine eigenen.
        // 🔑 Seit 18.09.2026 vier Unterkategorien statt einer Seite mit fünf
        // Blöcken (Design „Vertretungen v3"). Der Zweck ist die Reiterzeile:
        // kein Ort trägt mehr als drei Reiter, und jeder beantwortet EINE Frage.
        // Der Gruppenklick öffnet das Dashboard — das ist die alte Adresse, also
        // brechen die bestehenden Verweise (Meetings-Kalender, Sidebar-Badge)
        // nicht.
        key: "vertretung",
        label: "Vertretungen",
        iconKey: "user-check",
        views: [
          { key: "vertretung", label: "Dashboard", route: "/meetings/vertretung", hideAsSubTab: true },
          { key: "vertretung-klaerung", label: "Klärung", route: "/meetings/vertretung/klaerung" },
          { key: "vertretung-doppel", label: "Doppelbelegungen", route: "/meetings/vertretung/doppelbelegungen" },
          { key: "vertretung-regeln", label: "Regeln & Hosts", route: "/meetings/vertretung/regeln" }
        ]
      },
      {
        key: "closings",
        label: "Closings",
        iconKey: "check-circle",
        views: [
          { key: "closings", label: "Übersicht", route: "/closings", hideAsSubTab: true },
          { key: "closings-liste", label: "Liste", route: "/closings/liste" },
          // B3 (Justin, 26.08.): *„deswegen als eigenen punkt unter closings in
          // der seitenleiste"*. 🔑 Der Vertrieb legt Closings nicht von Hand an —
          // sie entstehen aus der Bestellabwicklung. Ein Terminier-Weg, der nur
          // im Closing-Detail sässe, käme deshalb nie jemandem unter.
          // 🔑 EIN Eintrag statt zwei (Justin, 30.08.). Weg B („Termin buchen")
          // und Weg A („VE & TF anstoßen") führen zum selben Ziel — zwei
          // Sidebar-Einträge waren einer zu viel. Sie liegen jetzt als zwei
          // Reiter auf einer Seite.
          //
          // ⚠️ **Buchen steht vorn**, und das ist kein Geschmack: es ist der
          // Griff im laufenden Telefonat. Die Liste ist das Nacharbeiten
          // danach — wer sie zuerst sieht, sucht im Gespräch am falschen Ort.
          // 🔄 Spec B · B-3 (08.10.2026): nur noch die TF — „Trainereinführung buchen".
          { key: "closings-terminierung", label: "Trainereinführung buchen", route: "/closings/terminierung" }
          // 🔄 18.09.2026: „Widerrufe" ist von hier in eine EIGENE Gruppe
          // gezogen (s. `widerrufsGruppe`). Er stand unter Closings, weil der
          // Widerruf-Zustand am Closing hängt — richtig für die Herkunft,
          // falsch für die Arbeit: dahinter liegen zwei ganz verschiedene
          // Fragen (Vorgänge · Prüfung), und die passten in keine Zeile.
        ]
      },
      widerrufsGruppe("/widerrufe"),
      kuendigungsGruppe("/kuendigungen"),
      vertragsanpassungsGruppe("/vertragsanpassungen"),
      {
        key: "funnel",
        label: "Funnel",
        iconKey: "bar-chart-3",
        views: [
          // 🔑 Die nackte Container-Route. Sie ist die EINZIGE Adresse, die der
          // Präfix-Rückfall in `findViewForRoute` nicht auflösen kann: die Nav
          // kennt nur das LÄNGERE `/funnel/status`, und ein längerer Eintrag ist
          // nie Präfix eines kürzeren Pfades. Ohne diese Zeile stünde `/funnel`
          // als einzige Route ohne Sub-Sidebar da. Gleiches Muster wie bei
          // `/caller`, `/meetings`, `/fu` und `/closings`.
          { key: "funnel-uebersicht", label: "Übersicht", route: "/funnel", hideAsSubTab: true },
          { key: "funnel-status", label: "Status", route: "/funnel/status", hideAsSubTab: true },
          // 🔄 A6 05.10.2026: die Stufe „Vereinseinrichtung" lebt IN der Performance
          // (Entscheid Justin) — die eigene Karte „Neuer Prozess" ist entfallen.
          { key: "funnel-performance", label: "Performance", route: "/funnel/performance" }
        ]
      },
      {
        // Eigene Oberkategorie (Justin 17.08.: „wichtig ist aber, dass das im
        // vertrieb liegen muss und nicht in kb — geht auch als eigene kategorie
        // im bereich vertrieb"). Die Daten kommen aus dem Vereinsbestand, die
        // FRAGE ist eine Vertriebsfrage: „habt ihr Partnervereine in der Nähe?"
        key: "partnervereine",
        label: "Partnervereine",
        iconKey: "map-pin",
        views: [
          { key: "partnervereine", label: "Liste & Umkreis", route: "/vertrieb/partnervereine" },
          { key: "partnervereine-karte", label: "Karte", route: "/vertrieb/partnervereine/karte" }
        ]
      },
      {
        key: "vertriebshistorie-group",
        label: "Vertriebshistorie",
        iconKey: "history",
        views: [
          { key: "vertriebshistorie", label: "Vertriebshistorie", route: "/vertriebshistorie" }
        ]
      },
      {
        // Bereichs-Einstellungen (Phase 7.4, D3): adminOnly=true ⇒ via
        // visibleGroupsForUser (isBlOrAdmin) sichtbar für BL Vertrieb + Admin,
        // NICHT für normale Caller/Hosts. Backend gated identisch
        // (mayEditBereichSettings(auth, "vertrieb")).
        key: "vertrieb-einstellungen",
        label: "Einstellungen",
        iconKey: "settings",
        adminOnly: true,
        isSettings: true,
        // Single-Link in der Sub-Sidebar: Group-Header-Klick → Container-Route
        // /vertrieb/einstellungen (Tab-Container). Alle Sub-Views hideAsSubTab,
        // damit KEINE Sub-Tab-Liste in der Sidebar erscheint (Nav-Umbau 2026-06-04).
        defaultRoute: "/vertrieb/einstellungen",
        views: [
          // Bare Container-Route (ohne Sub) → erster Tab. hideAsSubTab, damit es
          // nur die Bereich-Auflösung (findViewForRoute) füttert.
          { key: "vertrieb-einstellungen", label: "Einstellungen", route: "/vertrieb/einstellungen", hideAsSubTab: true },
          { key: "vertrieb-roster", label: "Caller & Hosts", route: "/vertrieb/einstellungen/roster", hideAsSubTab: true },
          { key: "vertrieb-slot-verfuegbarkeit", label: "Slot-Verfügbarkeit", route: "/vertrieb/einstellungen/slot-verfuegbarkeit", hideAsSubTab: true },
          { key: "vertrieb-slot-rotation", label: "Samstags-Rotation", route: "/vertrieb/einstellungen/slot-rotation", hideAsSubTab: true },
          { key: "vertrieb-schwellen", label: "Pflege-Schwellen", route: "/vertrieb/einstellungen/schwellen", hideAsSubTab: true },
          // Ausbaustufe 4: die Bewertung der Arena ist der Hebel fuer die Balance —
          // sie muss ohne Deploy aenderbar sein. Steht bewusst HIER und nicht unter
          // Admin/System: es ist eine Vertriebs-Entscheidung, keine Systemeinstellung,
          // und die Bereichsleitung Vertrieb soll sie ohne Admin-Rechte treffen koennen.
          { key: "vertrieb-arena-regeln", label: "Arena-Regeln", route: "/vertrieb/einstellungen/arena-regeln", hideAsSubTab: true },
          { key: "vertrieb-slack-templates", label: "Benachrichtigungen", route: "/vertrieb/einstellungen/slack-templates", hideAsSubTab: true },
          { key: "vertrieb-referenzen", label: "Lead-Status & Outcomes", route: "/vertrieb/einstellungen/referenzen", hideAsSubTab: true },
          { key: "vertrieb-lost-grund", label: "Lost-Gründe", route: "/vertrieb/einstellungen/lost-grund", hideAsSubTab: true },
          { key: "vertrieb-nachfolge", label: "Nachfolge", route: "/vertrieb/einstellungen/nachfolge", hideAsSubTab: true },
          { key: "vertrieb-uebergabe", label: "Übergabe", route: "/vertrieb/einstellungen/uebergabe", hideAsSubTab: true }
        ]
      }
    ],
    views: [] // wird im Code aus groups gefüttert (flattenedViews)
  },
  {
    slug: "kundenbetreuung",
    label: "Kundenbetreuung",
    short: "KB",
    iconKey: "life-buoy",
    color: "#06b6d4",
    requiresBereich: "kb",
    groups: [
      {
        // Direkt-Link (Single-View-Gruppe) — kein aufklappendes Untermenü mehr.
        key: "kb-dashboard-group",
        label: "Dashboard",
        iconKey: "layout-grid",
        views: [
          { key: "kb-dashboard", label: "Dashboard", route: "/kb/dashboard" }
        ]
      },
      {
        // P13c: KB-native Bestands-/Churn-Kennzahlen + Monatsreihe (Debug_Churn12) —
        // eigene Nav-Gruppe (Direkt-Link) statt Unterpunkt unter „Dashboard".
        key: "kb-bestand-group",
        label: "Bestand & Churn",
        iconKey: "trending-down",
        views: [
          { key: "kb-bestand", label: "Bestand & Churn", route: "/kb/bestand" }
        ]
      },
      {
        key: "kb-vereine-group",
        label: "Vereine",
        iconKey: "building-2",
        views: [
          { key: "kb-vereine", label: "Vereine", route: "/kb" }
        ]
      },
      {
        key: "kb-erfassung-group",
        label: "Gespräche",
        iconKey: "phone-call",
        views: [
          { key: "kb-erfassung", label: "Gespräch erfassen", route: "/kb/erfassung" }
        ]
      },
      {
        // P8: priorisierte Betreuungsliste („wen anrufen") + Betreuungs-KPI je Verein.
        key: "kb-betreuung-group",
        label: "Betreuung",
        iconKey: "trending-up",
        views: [
          { key: "kb-betreuung", label: "Betreuungsliste", route: "/kb/betreuung" }
        ]
      },
      {
        // Paket 3: Aufgaben-Engine — „was habe ich offen" (pro Phase, zugewiesen).
        key: "kb-aufgaben-group",
        label: "Aufgaben",
        iconKey: "list-checks",
        views: [
          { key: "kb-aufgaben", label: "Aufgaben", route: "/kb/aufgaben" }
          // 🔄 18.09.2026: „Widerrufe" hat eine eigene Gruppe (s. unten).
          // 🔑 Die Kundenbetreuung quittiert jeden Widerruf (Kenntnisnahme) —
          // sie braucht ihn in IHRER Navigation, und zwar so gegliedert wie
          // die anderen beiden Bereiche.
        ]
      },
      widerrufsGruppe("/kb/widerrufe"),
      kuendigungsGruppe("/kb/kuendigungen"),
      vertragsanpassungsGruppe("/kb/vertragsanpassungen"),
      {
        // P11: Zentralkalender — Onboarding-/Einführungstermine über alle Vereine.
        //
        // ═══ 🔑 DIE GRUPPE HEISST „VE UND TF TERMINE" (Justin, 31.08.) ═════════
        // Dritter Name in zwei Tagen, und der dritte ist der erste, der die
        // Sache benennt statt eines Bauteils: erst hieß sie „Kalender" (der
        // Name des ERSTEN Eintrags), dann „Termine" (zu weit — die KB hat auch
        // Betreuungs- und Aufgaben-Termine). Sie trägt genau zwei Terminarten,
        // und die stehen jetzt im Namen.
        //
        // 🔴 UND EIN ETIKETT WAR SCHLICHT FALSCH. `/kb/termine` hieß hier
        // „Termine buchen" — das ist aber der **Dashboard** der Terminstrecke;
        // gebucht wird unter `/kb/termine/buchen`. Wer dem Eintrag folgte,
        // landete auf einer Übersicht und suchte das Buchen-Formular.
        // *Ein Nav-Eintrag ist ein Versprechen über das, was hinter ihm liegt.*
        // ═══ 🔄 15.09.2026: JETZT „ONBOARDING-TERMINE" (Justin) ══════════════
        // *„Plus Namen: VE und TF Termine als Onboarding-Termine umbenennen."*
        //
        // 🔑 Der vierte Name, und er löst ein Problem, das die drei vorherigen
        // nicht hatten: **„VE und TF" sind Abkürzungen für Hausinterne.** Wer
        // neu dazukommt — und ab morgen arbeiten fünf Menschen damit —, liest
        // zwei Buchstabenpaare und weiss nicht, was dahinter liegt.
        // *„Onboarding" benennt den Vorgang; „VE und TF" benannte die Ablage.*
        //
        // ⚠️ Die beiden Terminarten heissen WEITER „Vereinseinrichtung" und
        // „Trainerfortbildung" — umbenannt wird der Sammelbegriff, nicht die
        // Sache. Die Felder in HubSpot bleiben ohnehin, wie sie sind.
        // ═══ 🔄 A6 Phase 2 · Spec B · B-2 (Justin 05.10.2026): „TRAINEREINFÜHRUNGEN" ═══
        // Die KB führt die Trainereinführung als eigenen Bereich und sieht keine
        // VEs mehr (die VE gehört dem Vertrieb, Gruppe „Vereinseinrichtungen").
        // Alle Schirme darunter stehen fest auf TF (`ve-ort.ts`, `ortTerminart`) —
        // bis auf die eine Ausnahme SB1: die Liste „Ohne VE (Altkunden)".
        key: "kb-termine-group",
        label: "Trainereinführungen",
        iconKey: "calendar-days",
        views: [
          // Der Einstieg in die Terminstrecke. Von hier führt ihre eigene
          // Reiterleiste weiter (Zu tun · Klärliste · Kalender · Buchen · …) —
          // acht Sidebar-Zeilen wären mehr Menü als Inhalt.
          { key: "kb-ve-termine", label: "Dashboard", route: "/kb/termine" },
          // ⚠️ Der ZENTRALKALENDER, nicht der Terminstrecken-Kalender. Zwei
          // verschiedene Quellen: dieser liest die Vereins-Stammdaten, jener
          // `ve_termin`. Der Unterschied zwischen beiden IST die Übernahme-
          // Lücke — 📏 gemessen am 31.08.: 300+ Vereine gegen EINE Terminzeile.
          { key: "kb-kalender", label: "Kalender", route: "/kb/kalender" },
          // 🆕 31.08. — derselbe Ort wie der Reiter „Ergebnisse", und ABSICHTLICH
          // derselbe Name. 🔑 Zwei Wege zum selben Schirm sind kein Fehler; zwei
          // NAMEN für denselben Schirm sind einer — dann sucht später jemand zwei
          // verschiedene Dinge und findet eines davon nie.
          { key: "kb-ve-ergebnisse", label: "Ergebnisse", route: "/kb/termine/ergebnisse" },
          // 🔴 31.08. — der Eintrag, der am teuersten gefehlt hat. „Meine Zeiten“
          // liegt auf dem alten Schirm, und dessen Adresse `/kb/termine/alt` war
          // seit dem Umbau UNERREICHBAR (die Parameter-Route fing sie ab). Ohne
          // ihn konnte KEIN Host seine Fenster eintragen — das erklaert die null
          // TF-Fenster, die tagelang als Betriebsstand gefuehrt wurden.
          // 🔑 *Eine Funktion, die nur ueber einen Schirm namens ‚alt‘ erreichbar
          // ist, ist praktisch nicht vorhanden.*
          { key: "kb-ve-zeiten", label: "Meine Zeiten", route: "/kb/termine/zeiten" },
          // 🆕 02.09. (B5) — die Zuteilung der Bereichsleitung. Sie steht NEBEN
          // "Meine Zeiten", weil beide dieselbe Sache von zwei Seiten sind:
          // dort traegt ein Host ein, hier wird entschieden, wofuer.
          { key: "kb-ve-zuteilung", label: "Zuteilung", route: "/kb/termine/zuteilung" }
        ]
      },
      {
        key: "kb-kontakte-group",
        label: "Kontakte",
        iconKey: "users",
        views: [
          { key: "kb-kontakte", label: "Kontakte & Trainer", route: "/kb/kontakte" }
        ]
      },
      {
        key: "kb-sonderaktionen-group",
        label: "Sonderaktionen",
        iconKey: "sparkles",
        views: [
          { key: "kb-sonderaktionen", label: "Sonderaktionen", route: "/kb/sonderaktionen" }
        ]
      },
      {
        // Einstellungen (P0b): für alle KB-User sichtbar; Bearbeiten backend-gegated
        // (Admin/Bereichsleiter), sonst read-only. Kein isSettings-Container (v1).
        key: "kb-einstellungen-group",
        label: "Einstellungen",
        iconKey: "settings",
        views: [
          { key: "kb-einstellungen", label: "Einstellungen", route: "/kb/einstellungen" },
          // E10: KB-BL pflegt die Rolle `kb_betreuer`; der Vertriebs-Container ist
          // für ihn nicht erreichbar (ACL = BL Vertrieb) → eigener Einstieg.
          { key: "kb-nachfolge", label: "Nachfolge", route: "/kb/nachfolge" },
          { key: "kb-uebergabe", label: "Übergabe", route: "/kb/uebergabe" }
        ]
      }
    ],
    views: []
  },
  {
    slug: "finance",
    label: "Finance",
    short: "Fin",
    iconKey: "wallet",
    color: "#f59e0b",
    // F0: Bereich live. Jeder MA mit Bereich „finance" sieht ihn (inkl. GF read-only);
    // Bearbeiten/Setup ist backend-gegated (Bereichsleiter „finance"/Admin).
    requiresBereich: "finance",
    // Subsidebar v3 (Claude-Design): 5 Sektionen mit per-View-Icon + per-View-Tag.
    // Rein additiv — Rendering-Model (Group-Header + Sub-Tabs) unverändert; nur
    // ViewDef.iconKey/tag wird zusätzlich genutzt. Keys/Routes bestehender Views
    // unverändert → Router löst weiter auf. NEU: finance-erfassung, finance-vereinsabo-offen.
    groups: [
      {
        // Überblick: Cockpit + Top-KPIs (MRR, Forderungen, Margen).
        key: "finance-ueberblick-group",
        label: "Überblick",
        iconKey: "layout-grid",
        views: [
          { key: "finance-uebersicht", label: "Cockpit", route: "/finance", iconKey: "gauge", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-mrr", label: "MRR-Analyse", route: "/finance/mrr", iconKey: "git-merge", tag: { label: "F7", kind: "f" } },
          { key: "finance-forderungen", label: "Forderungen & DSO", route: "/finance/forderungen", iconKey: "receipt", tag: { label: "F6", kind: "f" } },
          { key: "finance-margen", label: "Margen", route: "/finance/margen", iconKey: "percent", tag: { label: "F6", kind: "f" } }
          // 🔄 18.09.2026: „Widerrufe" hat eine eigene Gruppe (s. unten).
          // 🔴 Er gehört in diesen Bereich, weil er eine RECHNUNGSFRAGE ist:
          // bei TSV Dietmannsried wurden 101,09 € vier Tage NACH dem Widerruf
          // eingezogen. Wer die Rechnungen macht, muss die offenen Fälle sehen,
          // ohne sie im Vertriebs-Bereich suchen zu müssen.
        ]
      },
      widerrufsGruppe("/finance/widerrufe"),
      kuendigungsGruppe("/finance/kuendigungen"),
      vertragsanpassungsGruppe("/finance/vertragsanpassungen"),
      {
        // 🆕 02.11.2026 — SEPA-Papiermandate (Entwurf Mystique, Ort entschieden von Justin 02.11. 19:21:
        // Finance › „SEPA-Papiermandate“ mit Zähler). Die Prüfmaske `/finance/sepa-papier/:scanId`
        // steht nur im Router; der Präfix-Rückfall hebt diesen Eintrag hervor.
        key: "finance-sepa-papier-group",
        label: "SEPA-Papiermandate",
        iconKey: "file-signature",
        requiresBl: "finance",
        views: [{ key: "finance-sepa-papier", label: "SEPA-Papiermandate", route: "/finance/sepa-papier" }]
      },
      // 🔄 08.10.2026 (Justin): „Business-Case" ist in den Bereich Investor gezogen —
      // zusammen mit der neuen Gruppe „Investoren" (Update + Archiv). Die Adressen
      // bleiben /finance/…: die Navigation loest per exakter Route auf, Links halten.
      {
        // Buchhaltung: Konten/Kontobuch/Erfassung + Kostenstellen + Personal-Finance.
        key: "finance-buchhaltung-group",
        label: "Buchhaltung",
        iconKey: "book-open",
        views: [
          { key: "finance-konten", label: "Konten", route: "/finance/konten", iconKey: "landmark" },
          { key: "finance-buchhaltung", label: "Kontobuch", route: "/finance/buchhaltung", iconKey: "book-open" },
          { key: "finance-erfassung", label: "Erfassung", route: "/finance/erfassung", iconKey: "plus-circle" },
          { key: "finance-kostenstellen", label: "Kostenstellen", route: "/finance/kostenstellen", iconKey: "boxes", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-personal", label: "Personal-Finance", route: "/finance/personal", iconKey: "users", tag: { label: "NEU", kind: "neu" } }
        ]
      },
      {
        // Vereinsabos: Übersicht + ausstehende Posten.
        key: "finance-vereinsabos-group",
        label: "Vereinsabos",
        iconKey: "wallet",
        views: [
          { key: "finance-vereinsabo", label: "Übersicht", route: "/finance/vereinsabo", iconKey: "building-2", tag: { label: "F1", kind: "f" } },
          { key: "finance-vereinsabo-offen", label: "Ausstehende Posten", route: "/finance/vereinsabo/offen", iconKey: "alert-circle", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-vereinsabo-geplant", label: "Geplante Abos", route: "/finance/vereinsabo/geplant", iconKey: "calendar-clock", tag: { label: "NEU", kind: "neu" } }
        ]
      },
      {
        // Weitere: One-Pager + Preis-Sync + Taxonomie + Einstellungen (Mahn-Vorlage).
        key: "finance-weitere-group",
        label: "Weitere",
        iconKey: "layers",
        views: [
          { key: "finance-reporting", label: "One-Pager", route: "/finance/reporting", iconKey: "file-text" },
          { key: "finance-preis-sync", label: "Preis-Sync", route: "/finance/preis-sync", iconKey: "refresh-cw", tag: { label: "F8", kind: "f" } },
          { key: "finance-taxonomie", label: "Taxonomie", route: "/finance/taxonomie", iconKey: "tags" },
          { key: "finance-einstellungen", label: "Einstellungen", route: "/finance/einstellungen", iconKey: "settings" }
        ]
      }
    ],
    views: []
  },
  {
    slug: "hr",
    label: "HR",
    short: "HR",
    iconKey: "users",
    color: "#ec4899",
    requiresBl: "hr", // Admin oder HR-Leiter (Bereichsleiter "hr")
    // Jede View als eigener Eintrag (Single-View-Gruppen → eigenes Icon, keine
    // Sammel-Gruppen). Justin will jede View einzeln in der Sidebar (2026-06-16).
    groups: [
      { key: "hr-uebersicht", label: "Übersicht", iconKey: "layout-grid", views: [{ key: "hr-uebersicht", label: "Übersicht", route: "/hr" }] },
      { key: "hr-urlaub", label: "Urlaub", iconKey: "palmtree", views: [{ key: "hr-urlaub", label: "Urlaub", route: "/hr/urlaub" }] },
      { key: "hr-zeit", label: "Zeiten", iconKey: "clock", views: [{ key: "hr-zeit", label: "Zeiten", route: "/hr/zeit" }] },
      { key: "hr-abwesenheiten", label: "Abwesenheiten", iconKey: "heart-pulse", views: [{ key: "hr-abwesenheiten", label: "Abwesenheiten", route: "/hr/abwesenheiten" }] },
      { key: "hr-mitarbeiter", label: "Mitarbeiter", iconKey: "users", views: [{ key: "hr-mitarbeiter", label: "Mitarbeiter", route: "/hr/mitarbeiter" }] },
      // 🆕 09.10.2026 (HR-Abgleich Posten 6, Entwurf A1): was die nächtlichen Abgleiche geändert
      // haben und was zu entscheiden ist. Die vier Unterreiter (/vorschau, /protokoll, /lohnlaeufe,
      // /provision) stehen NUR im Router: der Präfix-Rückfall hebt diesen Eintrag hervor — fünf
      // Einträge hier machten daraus eine Gruppe, in der „Abgleich" zweimal stünde.
      { key: "hr-abgleich", label: "Abgleich", iconKey: "git-compare", views: [{ key: "hr-abgleich", label: "Abgleich", route: "/hr/abgleich" }] },
      { key: "hr-einstellungen", label: "Einstellungen", iconKey: "settings", views: [{ key: "hr-einstellungen", label: "Einstellungen", route: "/hr/einstellungen" }] }
    ],
    views: []
  },
  {
    slug: "app",
    label: "App",
    short: "App",
    iconKey: "smartphone",
    color: "#8b5cf6",
    // E9/P4 (2026-07-29): live. Store-Kennzahlen aus Google Play + App Store.
    // KEIN requires*-Gate: lesen darf jeder Mitarbeiter — die App ist das
    // gemeinsame Produkt, und die automatische Einordnung schützt besser vor
    // Fehlinterpretation als das Verstecken der Zahlen. Pflege ist backend-
    // seitig auf Admin gegated (`/api/v1/appstore/*`), die Pflege-Views tragen
    // zusätzlich `adminOnly` (Sidebar-Filter).
    groups: [
      { key: "app-uebersicht", label: "Übersicht", iconKey: "layout-grid", views: [{ key: "app-uebersicht", label: "Übersicht", route: "/app" }] },
      { key: "app-bewertungen", label: "Bewertungen", iconKey: "message-square", views: [{ key: "app-bewertungen", label: "Bewertungen", route: "/app/bewertungen" }] },
      { key: "app-kennzahlen", label: "Kennzahlen", iconKey: "table", views: [{ key: "app-kennzahlen", label: "Kennzahlen", route: "/app/kennzahlen", adminOnly: true }] },
      { key: "app-import", label: "Import", iconKey: "upload", views: [{ key: "app-import", label: "Import", route: "/app/import", adminOnly: true }] },
      { key: "app-einstellungen", label: "Einstellungen", iconKey: "settings", views: [{ key: "app-einstellungen", label: "Einstellungen", route: "/app/einstellungen", adminOnly: true }] }
    ],
    views: []
  },
  {
    slug: "dq",
    label: "Data-Quality",
    short: "DQ",
    iconKey: "shield-check",
    color: "#22c55e",
    requiresAdmin: true, // DQ = reines Admin-Tool (Justin 2026-05-30), passt zum admin-only Backend
    views: [{ key: "dq", label: "DQ-Übersicht", route: "/dq", adminOnly: true }]
  },
  {
    slug: "investor",
    label: "Investor",
    short: "Inv",
    iconKey: "presentation",
    color: "#64748b",
    // 🔄 08.10.2026 (Justin): aktiv — „gehört nicht in Finance, sondern in den Bereich
    // Investoren, genauso wie der Business-Case". Die Adressen bleiben /finance/…
    // (exakte Routen-Aufloesung in findViewForRoute), die Rechte der Endpunkte auch.
    // 🔴 Gate Pflicht: ohne `requiresBereich` saehe JEDER Nutzer den Bereich
    // (isBereichLiveForUser: kein Gate ⇒ true) — Investorenzahlen fuer alle.
    requiresBereich: "investor",
    views: [],
    groups: [
      {
        key: "investor-updates-group",
        label: "Investoren",
        iconKey: "presentation",
        views: [
          // Investoren-Automatik: rechnet je Zeitraum, was das Archiv beim Versand einfriert.
          { key: "finance-investor-update", label: "Investoren-Update", route: "/finance/investor-update", iconKey: "presentation", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-investor-archiv", label: "Investoren-Archiv", route: "/finance/investor-archiv", iconKey: "database", tag: { label: "NEU", kind: "neu" } }
        ]
      },
      {
        // Business-Case: Plan/Annahmen/Soll-Ist + Monatsabschluss + Reforecast + Ziele.
        key: "finance-business-case-group",
        label: "Business-Case",
        iconKey: "trending-up",
        views: [
          { key: "finance-plan", label: "Plan (GuV)", route: "/finance/plan", iconKey: "table-2", tag: { label: "F3", kind: "f" } },
          { key: "finance-plan-annahmen", label: "Annahmen", route: "/finance/plan/annahmen", iconKey: "sliders-horizontal" },
          { key: "finance-soll-ist", label: "Soll-Ist", route: "/finance/soll-ist", iconKey: "git-compare" },
          // Stufe 1b (W1-FI-10): der Abschluss macht einen Monat fest — die Grundlage
          // dessen, was das Investoren-Archiv als ausgestellt fuehrt.
          { key: "finance-monatsabschluss", label: "Monatsabschluss", route: "/finance/monatsabschluss", iconKey: "lock", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-reforecast", label: "Reforecast", route: "/finance/reforecast", iconKey: "git-fork", tag: { label: "NEU", kind: "neu" } },
          { key: "finance-ziele", label: "Ziele", route: "/finance/ziele", iconKey: "target", tag: { label: "NEU", kind: "neu" } }
        ]
      }
    ]
  },
  {
    slug: "admin",
    label: "Admin",
    short: "Adm",
    iconKey: "settings",
    color: "#94a3b8",
    requiresAdminOrBl: true,
    // 2-stufig (Gruppe → Sub-View, analog Vertrieb): Header-Klick → Haupt-View
    // (hideAsSubTab), darunter die Sub-Tabs. (Justin 2026-06-03)
    groups: [
      {
        key: "admin-mitarbeiter",
        label: "Mitarbeiter",
        iconKey: "user-circle",
        views: [
          { key: "admin-ma", label: "Verwaltung", route: "/admin/ma", hideAsSubTab: true },
          { key: "admin-berechtigungen", label: "Berechtigungen", route: "/admin/berechtigungen" },
          { key: "admin-zugriffsanfragen", label: "Zugriffsanfragen", route: "/admin/zugriffsanfragen", adminOnly: true }
        ]
      },
      {
        key: "admin-system",
        label: "System",
        iconKey: "settings",
        adminOnly: true,
        views: [
          { key: "admin-settings", label: "Einstellungen", route: "/admin/settings", hideAsSubTab: true },
          { key: "admin-system-format", label: "Datumsformat", route: "/admin/system/datumsformat" },
          { key: "admin-feiertage", label: "Feiertage", route: "/admin/system/feiertage" },
          { key: "admin-sheets", label: "Sheet-Inventar", route: "/admin/sheets" },
          // R7 WP5 — Gate-Cockpit: read-only Ist-Zustand aller Feature-/Cutover-Gates.
          { key: "admin-gates", label: "Gates", route: "/admin/gates", adminOnly: true }
        ]
      },
      {
        key: "admin-integrationen",
        label: "Integrationen",
        iconKey: "external-link",
        adminOnly: true,
        views: [
          // E12b-2: zweiter Reiter — deshalb ist `hideAsSubTab` hier weg, sonst
          // wäre die Automatik-Sicht nur per URL erreichbar.
          { key: "admin-integrations", label: "Übersicht", route: "/admin/system/integrationen" },
          { key: "admin-automationen", label: "Automatiken", route: "/admin/system/automatiken" }
        ]
      },
      {
        key: "admin-daten",
        label: "Companies-Cache",
        iconKey: "database",
        adminOnly: true,
        views: [
          { key: "admin-companies-cache", label: "Companies-Cache", route: "/admin/system/companies-cache", hideAsSubTab: true }
        ]
      },
      {
        key: "admin-protokoll",
        label: "Protokoll",
        iconKey: "history",
        adminOnly: true,
        views: [
          { key: "admin-audit", label: "Audit-Log", route: "/admin/audit", hideAsSubTab: true }
        ]
      }
      // Slack-Texte + Lost-Gründe: re-homed nach Vertrieb → Einstellungen (D3, 7.4)
    ],
    views: []
  }
];

// =============================================================================
// Filter-Helpers
// =============================================================================

/** Liefert flat-Views eines Bereichs (egal ob aus groups oder direkt views). */
export function flattenedViews(b: BereichDef): ViewDef[] {
  if (b.groups && b.groups.length > 0) {
    return b.groups.flatMap((g) => g.views);
  }
  return b.views;
}

/** Returnt ALLE Bereiche (auch coming-soon — die werden disabled gerendert). */
export function allBereicheForRail(): BereichDef[] {
  return BEREICHE;
}

/** Returnt nur die LIVE-zugänglichen Bereiche für den User (klickbar). */
export function liveBereicheForUser(user: UserContext): BereichDef[] {
  return BEREICHE.filter((b) => isBereichLiveForUser(b, user));
}

/**
 * ═══ 🆕 A6 Phase 2 · Spec B · F4 (Justin 05.10.2026) ═══════════════════════════
 * *„Maxi und Lennart (VE-Hosts) sehen den Bereich ‚Vereinseinrichtungen' in der
 * Sidebar, ohne Vertriebsbereich — sonst hätten sie nach diesem Umbau die Rechte,
 * aber keinen Weg zu ihren VEs."* Gilt für JEDEN aktiven VE-Host.
 *
 * ⇒ Der Vertrieb ist für einen VE-Host live, auch ohne Vertriebsbereich — aber er
 * sieht darin NUR die Gruppe „Vereinseinrichtungen" (`visibleGroupsForUser`).
 * Die Rechte dahinter prüft der Server je Terminart (`darfTerminart`, VE-Host ⇒
 * sehen/freigeben, Ergebnis am eigenen Termin) — die Leiste öffnet keinen Endpunkt.
 */
export const VE_HOST_GRUPPE = "vereinseinrichtungen";

/** Kommt der User NUR als VE-Host in den Vertrieb (kein Vertriebsbereich, kein Admin)? */
export function istNurVeHostImVertrieb(user: UserContext): boolean {
  if (!user.istVeHost || user.isAdmin) return false;
  const allowed = new Set(user.allowedBereiche.map((x) => x.toLowerCase()));
  return !allowed.has("*") && !allowed.has("vertrieb");
}

export function isBereichLiveForUser(b: BereichDef, user: UserContext): boolean {
  if (b.comingSoon) return false;
  if (b.requiresAdmin) return user.isAdmin;
  if (b.requiresAdminOrBl) return user.isAdmin || isAnyBl(user);
  if (b.requiresBl) return user.isAdmin || isBlOfBereich(user, b.requiresBl);
  if (!b.requiresBereich) return true;
  const allowed = new Set(user.allowedBereiche.map((x) => x.toLowerCase()));
  if (allowed.has("*") || allowed.has(b.requiresBereich.toLowerCase())) return true;
  // 🆕 Spec B · F4 — der VE-Host kommt in den Vertrieb (dort nur die Vereinseinrichtungen).
  return b.slug === "vertrieb" && istNurVeHostImVertrieb(user);
}

/**
 * 🆕 Spec B · F4 — wohin der Bereichs-Einstieg (Rail) führt. Für einen VE-Host
 * ohne Vertriebsbereich auf die Vereinseinrichtungen, sonst `null` (= wie bisher
 * die erste View des Bereichs).
 */
export function einstiegFuerUser(b: BereichDef, user: UserContext): string | null {
  if (b.slug !== "vertrieb" || !istNurVeHostImVertrieb(user)) return null;
  const g = b.groups?.find((x) => x.key === VE_HOST_GRUPPE);
  return g ? defaultRouteForGroup(g) : null;
}

/** Sichtbare flache Views (für Bereiche ohne groups, z.B. Hub/DQ). */
export function visibleViewsForUser(b: BereichDef, user: UserContext): ViewDef[] {
  const flat = flattenedViews(b);
  const isBlOrAdmin = user.isAdmin || isBlVertrieb(user);
  return flat.filter((v) => isViewVisibleForUser(v, user, isBlOrAdmin));
}

/** Sichtbare Groups (Vertrieb) inkl. gefilterter Sub-Views. */
export function visibleGroupsForUser(
  b: BereichDef,
  user: UserContext
): Array<GroupDef & { views: ViewDef[] }> {
  if (!b.groups || b.groups.length === 0) return [];
  const isBlOrAdmin = user.isAdmin || isBlVertrieb(user);
  // 🆕 Spec B · F4 — ein VE-Host ohne Vertriebsbereich sieht im Vertrieb NUR die
  // Gruppe „Vereinseinrichtungen" (Zuteilung bleibt adminOnly ⇒ ausgeblendet).
  const nurVeHost = b.slug === "vertrieb" && istNurVeHostImVertrieb(user);

  return b.groups
    .filter((g) => !nurVeHost || g.key === VE_HOST_GRUPPE)
    .filter((g) => {
      // Persönliche Gruppe: gilt strikt, OHNE Admin-Hintertür. Ein Admin ist
      // nicht automatisch Adressat einer persönlichen Ansicht.
      if (g.requiresPerson && user.person_id !== g.requiresPerson) return false;
      if (g.requiresBl && !(user.isAdmin || isBlOfBereich(user, g.requiresBl))) return false;
      if (g.adminOnly && !isBlOrAdmin) return false;
      if (g.requiresAktiv && g.requiresAktiv.length > 0) {
        const has = g.requiresAktiv.some((f) => user.aktiveFunktionen.includes(f));
        if (!has && !isBlOrAdmin) return false;
      }
      return true;
    })
    .map((g) => ({
      ...g,
      views: g.views.filter((v) => isViewVisibleForUser(v, user, isBlOrAdmin))
    }))
    .filter((g) => g.views.length > 0);
}

/**
 * Liefert die für den User SICHTBARE isSettings-Group eines Bereichs (inkl.
 * gefilterter Sub-Views) — oder null. Sichtbarkeit wie visibleGroupsForUser
 * (adminOnly → isBlOrAdmin). Wird vom Sub-Sidebar-Head (Zahnrad) + der
 * Einstellungs-Container-View genutzt.
 */
export function visibleSettingsGroupForUser(
  b: BereichDef,
  user: UserContext
): (GroupDef & { views: ViewDef[] }) | null {
  return visibleGroupsForUser(b, user).find((g) => g.isSettings) ?? null;
}

function isViewVisibleForUser(v: ViewDef, user: UserContext, isBlOrAdmin: boolean): boolean {
  // R7: Liga-Sichtbarkeit hängt am Laufzeit-Flag LIGA_LIVE (System-Settings) —
  // live überstimmt das statische adminOnly (Vorschau-Gate bleibt der Fallback).
  // 🔒 Baustein 4 (Justin 16.08.): Buchen + Anfragen sind vorerst NUR für die
  // serverseitige Freigabeliste sichtbar — dieselbe Liste, die auch die
  // Endpoints gatet. Eine ausgeblendete Kachel wäre allein keine Berechtigung;
  // deshalb steht dieselbe Regel im Worker.
  if ((v.key === "meetings-buchen" || v.key === "meetings-anfragen") && !istBuchungSichtbar()) return false;
  const adminOnly =
    v.key === "vertrieb-liga" ? !isLigaLive() : v.key === "vertrieb-arena" ? !isArenaLive() : v.adminOnly;
  if (adminOnly && !isBlOrAdmin) return false;
  if (v.requiresAktiv && v.requiresAktiv.length > 0) {
    const has = v.requiresAktiv.some((f) => user.aktiveFunktionen.includes(f));
    if (!has && !isBlOrAdmin) return false;
  }
  return true;
}

/**
 * ⚠️ Delegiert bewusst an `findViewForRoute` und rechnet nicht selbst.
 * Zwei Funktionen, die dieselbe Frage verschieden beantworten, sind eine
 * Falle für den nächsten Aufrufer: er bekäme hier die alte Auflösung ohne
 * Präfix-Rückfall und merkte es nicht.
 */
export function findBereichForRoute(path: string): BereichDef | null {
  return findViewForRoute(path)?.bereich ?? null;
}

/**
 * Löst eine Adresse auf den Nav-Eintrag auf, unter dem sie lebt.
 *
 * ═══ 🔑 ZWEI DURCHGÄNGE, UND DER ZWEITE IST DER WICHTIGE ═══════════════════
 * ① **Gleichheit** — der Regelfall, unverändert.
 * ② **Längster passender Präfix** — für jede Adresse, die kein eigener
 *    Nav-Eintrag ist: Unterwege (`/kb/termine/buchen`), Detailschirme
 *    (`/kb/verein/123`), Rückwege (`/widerrufe/alt`).
 *
 * ═══ 🔴 WARUM DAS EIN FEHLER WAR UND KEINE LÜCKE ═══════════════════════════
 * `app-shell.ts` blendet die **ganze** Sub-Sidebar aus, wenn hier `null`
 * zurückkommt (`display:none`) — nicht bloß die Hervorhebung. Eine Adresse
 * ohne Nav-Eintrag verlor damit die komplette Navigation: auf sechs der sieben
 * Terminstrecken-Schirme, auf jedem Vereins- und Mitarbeiterdetail und auf
 * allen Unterwegen des Widerruf-Neubaus (`…/melden`, `…/fall/:fallId`, je
 * dreimal für die drei Einstiege).
 *
 * 🔑 *Eine Regel schlägt einundzwanzig Einträge.* Die Alternative wäre gewesen,
 * jede Unteradresse als `hideAsSubTab`-View nachzutragen — das trägt aber nur
 * feste Pfade und nie einen `:id`-Parameter, und es hätte beim nächsten neuen
 * Unterweg wieder gefehlt, still.
 *
 * ⚠️ **Der längste Präfix gewinnt, quer über alle Bereiche.** Das ist die
 * einzige Reihenfolge, die den Widerruf-Neubau richtig auflöst:
 * `/kb/widerrufe/melden` muss auf `/kb/widerrufe` fallen (Kundenbetreuung) und
 * nicht auf `/kb` (Vereine) — sonst säße ein KB-Nutzer beim Melden unter der
 * falschen Gruppe. Ein Gleichstand ist unmöglich: zwei verschieden lange
 * Zeichenketten können nicht beide Präfix derselben Adresse sein.
 */
export function findViewForRoute(path: string): { bereich: BereichDef; view: ViewDef } | null {
  for (const b of BEREICHE) {
    for (const v of flattenedViews(b)) {
      if (routeMatches(v.route, path)) return { bereich: b, view: v };
    }
  }
  // ② Rückfall. Erst hier, nie vorher: ein exakter Treffer ist immer die
  // bessere Auskunft als ein Präfix, und `/` bleibt ausgenommen — sonst wäre
  // der Hub der Präfix von allem und jede Adresse landete dort.
  let treffer: { bereich: BereichDef; view: ViewDef } | null = null;
  let laenge = -1;
  for (const b of BEREICHE) {
    for (const v of flattenedViews(b)) {
      if (v.route === "/" || !path.startsWith(v.route + "/")) continue;
      if (v.route.length > laenge) {
        treffer = { bereich: b, view: v };
        laenge = v.route.length;
      }
    }
  }
  return treffer;
}

/** Liefert die Group eines Views (für Active-Highlight des Group-Headers). */
export function findGroupForView(b: BereichDef, viewKey: string | null): GroupDef | null {
  if (!b.groups || !viewKey) return null;
  for (const g of b.groups) {
    if (g.views.some((v) => v.key === viewKey)) return g;
  }
  return null;
}

/** Default-Sub-View einer Group (erster nicht-coming-soon View, sonst erster). */
export function defaultRouteForGroup(g: GroupDef): string {
  if (g.defaultRoute) return g.defaultRoute;
  const firstLive = g.views.find((v) => !v.comingSoon);
  if (firstLive) return firstLive.route;
  return g.views[0]?.route ?? "/";
}

function routeMatches(route: string, path: string): boolean {
  if (route === path) return true;
  if (route === "/" && path === "/hub") return true;
  if (route === "/hub" && path === "/") return true;
  return false;
}

/** True wenn User Bereichsleiter des angegebenen Bereichs ist (für Bereichs-Settings-Gating). */
export function isBlOfBereich(user: UserContext, bereich: string): boolean {
  const target = bereich.toLowerCase();
  return user.berechtigungen.some(
    (b) => b.rolle.toLowerCase() === "bereichsleiter" && b.bereich.toLowerCase() === target
  );
}

function isBlVertrieb(user: UserContext): boolean {
  return isBlOfBereich(user, "vertrieb");
}

/** True wenn User in IRGENDEINEM Bereich Bereichsleiter ist (Admin-Bereich-Sichtbarkeit 3.7). */
function isAnyBl(user: UserContext): boolean {
  return user.berechtigungen.some((b) => b.rolle.toLowerCase() === "bereichsleiter");
}
